// A signed-in user with no home on this platform (data-model §2; FR-026, FR-028; R-4, R-5): a
// school-admin-only account on Android is told the console is on the web, with a button that opens
// it; an account with no role this app serves gets the FR-026 message. Log out ends the session
// (USER). The screen is in the Akaiva sign-in frame, so it reads as part of the app, not an error
// page. No parent screen mounts and no request is made.
import { Linking, StyleSheet, View } from "react-native";

import { AUTH_COPY } from "@/auth/copy";
import { AuthCard } from "@/auth/ui/AuthCard";
import { readPublicConfig } from "@/config";
import { useSessionController } from "@/session/hooks";
import type { Landing } from "@/session/types";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { Text } from "@/ui/Text";

type Reason = Extract<Landing, { kind: "unsupported" }>["reason"];

const messages: Record<Reason, string> = {
  SCHOOL_ADMIN_ONLY_ON_ANDROID: AUTH_COPY.roles.schoolAdminOnWeb,
  NO_SERVED_ROLE: AUTH_COPY.refusals.noAccess,
};
const titles: Record<Reason, string> = {
  SCHOOL_ADMIN_ONLY_ON_ANDROID: "School admin is on the web",
  NO_SERVED_ROLE: "No access to this app",
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({ stack: { gap: t.space.md } }),
);

export function UnsupportedRoleScreen({ reason }: { reason: Reason }) {
  const styles = useStyles();
  const { signOut } = useSessionController();
  const openWeb = () => {
    try {
      void Linking.openURL(readPublicConfig().webBaseUrl);
    } catch {
      // No web address in this build: the message above still names it.
    }
  };
  return (
    <AuthCard title={titles[reason]} testID="unsupported-role-screen">
      <View style={styles.stack}>
        <Text tone="muted">{messages[reason]}</Text>
        {reason === "SCHOOL_ADMIN_ONLY_ON_ANDROID" ? (
          <Button onPress={openWeb} testID="unsupported-role-open-web">
            Open on the web
          </Button>
        ) : null}
        <Button
          variant="outline"
          onPress={() => void signOut("USER")}
          testID="unsupported-role-logout"
        >
          {AUTH_COPY.actions.logOut}
        </Button>
      </View>
    </AuthCard>
  );
}
